import DOMPurify from 'dompurify';

// Custom Error for Password Validation
export class PasswordValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PasswordValidationError';
    }
}

export function splitAndFormatText(response) {
    // Create a regular expression that matches any of the delimiters
    /**
     * (1) Delimiters: The regex is designed to match specific characters or sequences that act as delimiters in the text. Here’s what each part represents:
     * \|\/\|: (The sequence |/|)
     *      \|: The backslash \ is used to escape the pipe character |, which is a special character in regex. So, \| matches the literal |.
     *      \/: The backslash \ is used to escape the forward slash /, so \/ matches the literal / character.
     *      |:  This is the second pipe character that comes after the escaped sequence, so it matches another |.
     *      Together, \|\/\| matches the exact string |/| (with spaces).
     * 。: (The Chinese period 。)
     *          This matches the Chinese period character, which is used to end sentences in Chinese text.
     * ？: (The Chinese question mark ？)
     *          This matches the Chinese question mark, used in Chinese questions.
     * \?: (The standard question mark ?)
     *          This matches the standard question mark ?. The backslash is used to escape the question mark, which is a special character in regex.
     * \n: (The newline character \n)
     *          This matches a newline character, which is used to indicate a line break in the text.
     * 
     * (2) | (Pipe):
     * This is the "OR" operator in regex. It allows the regex to match any of the individual patterns on either side. For example, if the input contains any of the matched delimiters (e.g., |/|, 。, ？, ?, or a newline), it will be considered a match.
     * 
     * (3) g (Global Flag):
     * The g flag indicates that the regex should be applied globally to the entire string, meaning it will find all matches in the input string, not just the first one.
     */ 
    const regex = /\|\/\||。 \/| =>|\n/g;
    // Split the response using the regex
    const parts = response.split(regex);

    // Trim each part and filter out any empty strings
    const formattedParts = parts.map(part => part.trim()).filter(part => part.length > 0);

    // Return formatted parts as paragraphs
    return formattedParts.map((part, index) => <p key={index}>{part}</p>);
}

export const formatResponseToHTML = (responseText) => {
    const sections = responseText.split('\n\n').filter(section => section.trim() !== '');
    let htmlOutput = '';

    sections.forEach((section) => {
        const lines = section.split('\n').filter(line => line.trim() !== '');

        if (lines.some(line => line.includes('|'))) {
            htmlOutput += '<table>';
            htmlOutput += '<thead><tr>';
            const headerLine = lines[0].split('|').map(item => item.trim()).filter(item => item);
            headerLine.forEach(header => {
                if (header) {
                    htmlOutput += `<th>${DOMPurify.sanitize(header)}</th>`;
                }
            });
            htmlOutput += '</tr></thead>';
            htmlOutput += '<tbody>';

            for (let i = 2; i < lines.length; i++) {
                const columns = lines[i].split('|').map(item => item.trim()).filter(item => item);
                if (columns.length > 0) {
                    htmlOutput += '<tr>';
                    columns.forEach(col => {
                        htmlOutput += `<td>${DOMPurify.sanitize(col)}</td>`;
                    });
                    htmlOutput += '</tr>';
                }
            }
            htmlOutput += '</tbody></table>';
        } else {
            lines.forEach((line) => {
                line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                // Function to replace lines starting with ## and ending with ：
                htmlOutput += `<p>${DOMPurify.sanitize(line).replace(/## (.*?)：/g, '<strong>$1：</strong>').replace(/## (.*?):/g, '<strong>$1:</strong>')}</p>`;
            });
        }
    });

    return htmlOutput;
};
/*
export const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const adjustedDate = new Date(date.getTime() - 8 * 60 * 60 * 1000); // Create a new date object adjusted by -8 hours
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    const hours = String(adjustedDate.getHours()).padStart(2, '0');
    const minutes = String(adjustedDate.getMinutes()).padStart(2, '0');
    const seconds = String(adjustedDate.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};*/
export const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
 //   date.setHours(date.getHours() + 8); // Adds 8 hours to the date
    return date.toISOString().slice(0, 19).replace('T', ' '); // Format as "yyyy-MM-dd HH:mm:ss"
};

export const formatYearMonth = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
   // date.setHours(date.getHours() + 8); // Adds 8 hours to the date

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based

    return `${year}-${month}`; // Format as "YYYY-MM"
};

export const formatYearMonthDate = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
   // date.setHours(date.getHours() + 8); // Adds 8 hours to the date

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // Format as "YYYY-MM-dd"
};

export const formatMonth = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
   // date.setHours(date.getHours() + 8); // Adds 8 hours to the date

    const monthNames = [
        'Jan', 'Feb', 'Mar',
        'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep',
        'Oct', 'Nov', 'Dec'
    ];

    const month = monthNames[date.getMonth()]; // Get the month abbreviation
    return month; // Return the month as "MMM"
};

export const formatDate = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
   // date.setHours(date.getHours() + 8); // Adds 8 hours to the date

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');

    return `${day}`; // Format as "dd"
};

export const formatLongDate = (dateTime) => {
    // Ensure the input is a valid Date object
    const hktDate = new Date(dateTime);
    if (isNaN(hktDate.getTime())) {
        throw new TypeError("Invalid date input");
    }
     // Get individual components
     const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
     const month = months[hktDate.getMonth()];
     const day = hktDate.getDate();
     const year = hktDate.getFullYear();
     
     // Format hours and minutes
     let hours = hktDate.getHours();
     const minutes = hktDate.getMinutes();
     const ampm = hours >= 12 ? 'PM' : 'AM';
     hours = hours % 12;
     hours = hours ? hours : 12; // the hour '0' should be '12'
 
     const formattedTime = `${hours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;

     // Calculate relative time
     const now = new Date();
     const diffInHours = Math.floor((now - hktDate) / (1000 * 60 * 60));
     const diffInDays = Math.floor(diffInHours / 24);
     
     let relativeTime;
     if (diffInHours < 1) {
         relativeTime = 'just now';
     } else if (diffInDays === 0) {
         relativeTime = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
     } else {
         relativeTime = `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
     }
 
     // Combine all parts
     return `${month} ${day}, ${year}, ${formattedTime} (${relativeTime})`;
}

export const formatDisplayDate = (dateTime) => {
    const now = new Date();
    const receivedDate = new Date(dateTime);

    // Check if the received date is today
    const isToday = 
        now.getFullYear() === receivedDate.getFullYear() &&
        now.getMonth() === receivedDate.getMonth() &&
        now.getDate() === receivedDate.getDate();

    if (isToday) {
        // Format time as HH:MM AM/PM
        const hours = receivedDate.getHours();
        const minutes = receivedDate.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedTime = `${hours % 12 || 12}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
        return formattedTime;
    } else {
        // Format date as Mon DD
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[receivedDate.getMonth()];
        const day = receivedDate.getDate();
        return `${month} ${day}`;
    }
}

export const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`; // Format as YYYYMMdd
};

// Validates password strength
export const passwordValidator = (value) => {
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/;
    if (!passwordPattern.test(value)) {
        throw new PasswordValidationError('Password must be 8-20 characters long, contain at least one uppercase letter, one lowercase letter, one numeric digit, and one special character.');
    }
    return true;
};